import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { supplierService, SupplierDTO, UpdateSupplierRequest } from '../../services/supplierService';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import '../Products/ProductCategoryEditPage.css';
import { ArrowLeft, Save, Eye, ShoppingBag } from 'lucide-react';

const MAX_LENGTHS = {
  name: 150,
  contactEmail: 150,
  contactPhone: 100,
  address: 500,
  city: 100,
  state: 100,
  postalCode: 50,
  country: 100,
};

interface FieldErrors {
  name?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  creditPeriodDays?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PAYMENT_TERMS_CASH = 'Cash';
const PAYMENT_TERMS_CREDIT = 'Credit';
const MAX_CREDIT_PERIOD_DAYS = 365;

const SupplierEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [supplier, setSupplier] = useState<SupplierDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<UpdateSupplierRequest>({
    code: '',
    name: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    paymentTerms: '',
    creditPeriodDays: 0,
    isActive: true,
  });
  const [creditDaysInput, setCreditDaysInput] = useState('');

  useEffect(() => {
    loadSupplier();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSupplier = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await supplierService.getSupplierById(id || '0');
      if (response?.data?.data) {
        const sup = response.data.data;
        setSupplier(sup);
        setFormData({
          code: sup.code,
          name: sup.name,
          contactEmail: sup.contactEmail,
          contactPhone: sup.contactPhone || '',
          address: sup.address || '',
          city: sup.city || '',
          state: sup.state || '',
          postalCode: sup.postalCode || '',
          country: sup.country || '',
          paymentTerms: sup.paymentTerms || '',
          creditPeriodDays: sup.creditPeriodDays || 0,
          isActive: sup.isActive,
        });
        setCreditDaysInput(
          sup.paymentTerms === PAYMENT_TERMS_CREDIT && sup.creditPeriodDays
            ? String(sup.creditPeriodDays)
            : ''
        );
      }
      setLoading(false);
    } catch {
      setError(t('supplier.failedToLoadSupplier'));
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (name === 'isActive') {
      setFormData((prev) => ({ ...prev, isActive: value === 'true' }));
      return;
    }

    if (name === 'paymentTerms') {
      setFormData((prev) => ({
        ...prev,
        paymentTerms: value,
        creditPeriodDays: value === PAYMENT_TERMS_CREDIT ? prev.creditPeriodDays : 0,
      }));
      if (value !== PAYMENT_TERMS_CREDIT) setCreditDaysInput('');
      setFieldErrors((prev) => ({ ...prev, creditPeriodDays: undefined }));
      return;
    }

    if (name === 'creditPeriodDays') {
      // Integer only — strip anything that is not a digit
      const digits = value.replace(/\D/g, '');
      setCreditDaysInput(digits);
      setFormData((prev) => ({ ...prev, creditPeriodDays: digits ? parseInt(digits, 10) : 0 }));
      setFieldErrors((prev) => ({ ...prev, creditPeriodDays: undefined }));
      return;
    }

    const isCheckbox = type === 'checkbox';
    if (isCheckbox) {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Name — required + max length
    if (!formData.name.trim()) {
      errors.name = t('supplier.formValidation.nameRequired');
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('supplier.formValidation.nameMaxLength');
      isValid = false;
    }

    // Contact email — required + format + max length
    if (!formData.contactEmail.trim()) {
      errors.contactEmail = t('supplier.formValidation.emailRequired');
      isValid = false;
    } else if (!EMAIL_REGEX.test(formData.contactEmail)) {
      errors.contactEmail = t('supplier.formValidation.emailInvalid');
      isValid = false;
    } else if (formData.contactEmail.length > MAX_LENGTHS.contactEmail) {
      errors.contactEmail = t('supplier.formValidation.emailMaxLength');
      isValid = false;
    }

    // Contact phone — optional, max length only
    if (formData.contactPhone && formData.contactPhone.length > MAX_LENGTHS.contactPhone) {
      errors.contactPhone = t('supplier.formValidation.phoneMaxLength');
      isValid = false;
    }

    // Address — optional, max length only
    if (formData.address && formData.address.length > MAX_LENGTHS.address) {
      errors.address = t('supplier.formValidation.addressMaxLength');
      isValid = false;
    }

    // City — optional, max length only
    if (formData.city && formData.city.length > MAX_LENGTHS.city) {
      errors.city = t('supplier.formValidation.cityMaxLength');
      isValid = false;
    }

    // State — optional, max length only
    if (formData.state && formData.state.length > MAX_LENGTHS.state) {
      errors.state = t('supplier.formValidation.stateMaxLength');
      isValid = false;
    }

    // Postal code — optional, max length only
    if (formData.postalCode && formData.postalCode.length > MAX_LENGTHS.postalCode) {
      errors.postalCode = t('supplier.formValidation.postalCodeMaxLength');
      isValid = false;
    }

    // Country — optional, max length only
    if (formData.country && formData.country.length > MAX_LENGTHS.country) {
      errors.country = t('supplier.formValidation.countryMaxLength');
      isValid = false;
    }

    // Credit period days — required integer when payment terms is Credit
    if (formData.paymentTerms === PAYMENT_TERMS_CREDIT) {
      if (!creditDaysInput || !formData.creditPeriodDays || formData.creditPeriodDays < 1) {
        errors.creditPeriodDays = t('supplier.formValidation.creditDaysRequired');
        isValid = false;
      } else if (formData.creditPeriodDays > MAX_CREDIT_PERIOD_DAYS) {
        errors.creditPeriodDays = t('supplier.formValidation.creditDaysMax', { max: MAX_CREDIT_PERIOD_DAYS });
        isValid = false;
      }
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm() || !supplier) return;

    setSaving(true);
    try {
      await supplierService.updateSupplier(supplier.id, formData);
      navigate(`/suppliers/${supplier.id}`, { state: { message: t('supplier.supplierUpdatedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('supplier.failedToUpdateSupplier')).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!supplier) {
    return (
      <Layout>
        <div className="edit-container">
          <div className="alert alert-danger">{error || t('supplier.supplierNotFound')}</div>
          <button className="edit-breadcrumb-btn" onClick={() => navigate('/suppliers')}>
            {t('supplier.backToSuppliers')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="edit-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate(`/suppliers/${id}`)}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {t('supplier.editTitle')}
              <span className="ims-page-subtitle">{supplier.name} — <code>{supplier.code}</code></span>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate(`/suppliers/${id}`)} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.saveChanges')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('supplier.supplierInfo')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>

                  {/* Supplier Code — read-only */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.supplierCode')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.code}
                      disabled
                    />
                    <small className="form-helper">{t('supplier.codeCannotChange')}</small>
                  </div>

                  {/* Supplier Name */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('supplier.supplierName')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.name}
                    />
                    {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                    {!fieldErrors.name && <small className="form-helper">{t('supplier.nameHint')}</small>}
                  </div>

                  {/* Contact Email */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('supplier.contactEmail')} <span className="required">*</span>
                      <span className="char-counter">{formData.contactEmail.length}/{MAX_LENGTHS.contactEmail}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.contactEmail ? 'is-invalid' : ''}`}
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.contactEmail}
                    />
                    {fieldErrors.contactEmail && <small className="form-error">{fieldErrors.contactEmail}</small>}
                    {!fieldErrors.contactEmail && <small className="form-helper">{t('supplier.contactEmailHint')}</small>}
                  </div>

                  {/* Contact Phone */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.contactPhone')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.contactPhone ? 'is-invalid' : ''}`}
                      name="contactPhone"
                      value={formData.contactPhone || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.contactPhone}
                    />
                    {fieldErrors.contactPhone && <small className="form-error">{fieldErrors.contactPhone}</small>}
                    {!fieldErrors.contactPhone && <small className="form-helper">{t('supplier.contactPhoneHint')}</small>}
                  </div>

                  {/* Address */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.address')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.address ? 'is-invalid' : ''}`}
                      name="address"
                      value={formData.address || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.address}
                    />
                    {fieldErrors.address && <small className="form-error">{fieldErrors.address}</small>}
                    {!fieldErrors.address && <small className="form-helper">{t('supplier.addressHint')}</small>}
                  </div>

                  {/* City */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.city')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.city ? 'is-invalid' : ''}`}
                      name="city"
                      value={formData.city || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.city}
                    />
                    {fieldErrors.city && <small className="form-error">{fieldErrors.city}</small>}
                    {!fieldErrors.city && <small className="form-helper">{t('supplier.cityHint')}</small>}
                  </div>

                  {/* State / Province */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.state')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.state ? 'is-invalid' : ''}`}
                      name="state"
                      value={formData.state || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.state}
                    />
                    {fieldErrors.state && <small className="form-error">{fieldErrors.state}</small>}
                    {!fieldErrors.state && <small className="form-helper">{t('supplier.stateHint')}</small>}
                  </div>

                  {/* Postal Code */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.postalCode')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.postalCode ? 'is-invalid' : ''}`}
                      name="postalCode"
                      value={formData.postalCode || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.postalCode}
                    />
                    {fieldErrors.postalCode && <small className="form-error">{fieldErrors.postalCode}</small>}
                    {!fieldErrors.postalCode && <small className="form-helper">{t('supplier.postalCodeHint')}</small>}
                  </div>

                  {/* Country */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.country')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.country ? 'is-invalid' : ''}`}
                      name="country"
                      value={formData.country || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.country}
                    />
                    {fieldErrors.country && <small className="form-error">{fieldErrors.country}</small>}
                    {!fieldErrors.country && <small className="form-helper">{t('supplier.countryHint')}</small>}
                  </div>

                  {/* Payment Terms */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.paymentTerms')}</label>
                    <select
                      className="form-select"
                      name="paymentTerms"
                      value={formData.paymentTerms || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('supplier.paymentTermsSelect')}</option>
                      <option value={PAYMENT_TERMS_CASH}>{t('supplier.paymentTermsCash')}</option>
                      <option value={PAYMENT_TERMS_CREDIT}>{t('supplier.paymentTermsCredit')}</option>
                    </select>
                    <small className="form-helper">{t('supplier.paymentTermsHint')}</small>
                  </div>

                  {/* Credit Period Days — only for Credit terms */}
                  {formData.paymentTerms === PAYMENT_TERMS_CREDIT && (
                    <div className="form-group">
                      <label className="form-label">
                        {t('supplier.creditPeriodDays')} <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`form-input ${fieldErrors.creditPeriodDays ? 'is-invalid' : ''}`}
                        name="creditPeriodDays"
                        value={creditDaysInput}
                        onChange={handleInputChange}
                        placeholder={t('supplier.creditPeriodDaysPlaceholder')}
                        maxLength={3}
                      />
                      {fieldErrors.creditPeriodDays && <small className="form-error">{fieldErrors.creditPeriodDays}</small>}
                      {!fieldErrors.creditPeriodDays && <small className="form-helper">{t('supplier.creditPeriodDaysHint')}</small>}
                    </div>
                  )}

                  {/* Status */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.status')}</label>
                    <select
                      className="form-select"
                      name="isActive"
                      value={formData.isActive ? 'true' : 'false'}
                      onChange={handleInputChange}
                    >
                      <option value="true">{t('supplier.active')}</option>
                      <option value="false">{t('supplier.inactive')}</option>
                    </select>
                  </div>

                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('supplier.auditInformation')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="info-item">
                  <span className="info-label">{t('supplier.supplierId')}</span>
                  <span className="info-value">{supplier.id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('supplier.status')}</span>
                  <span className="info-value">
                    <span className={`badge ${supplier.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {supplier.isActive ? t('supplier.active') : t('supplier.inactive')}
                    </span>
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('supplier.createdDate')}</span>
                  <span className="info-value">
                    {new Date(supplier.createdAt).toLocaleDateString(langCode)}
                  </span>
                </div>
              </div>
            </div>

            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('supplier.quickActions')}</h3>
              </div>
              <div className="edit-card-body">
                <button
                  type="button"
                  className="detail-quick-link"
                  onClick={() => navigate(`/suppliers/${supplier.id}`)}
                >
                  <Eye size={16} strokeWidth={2} />
                  {t('supplier.viewTitle')}
                </button>
                <button
                  type="button"
                  className="detail-quick-link"
                  onClick={() => navigate(`/purchase-items?supplierId=${supplier.id}`)}
                  style={{ marginTop: '8px' }}
                >
                  <ShoppingBag size={16} strokeWidth={2} />
                  {t('supplier.viewPurchaseItems')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SupplierEditPage;
